import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

import { RouterFacade } from '@core/shared/data-access';
import { Pagination, PaginationMetadata } from '@core/shared/domain';
import { NotificationService, QueryParams, filterNull, getProp } from '@core/shared/util';
import {
  IndustriekatalogeService,
  IndustryCatalog,
  IndustryCatalogFilter,
  IndustryCatalogInRelease,
  IndustryCatalogManufacturer,
  IndustryCatalogOpenTasks,
  IndustryCatalogSupplier,
  PackungseinheitenMapping,
  PackungseinheitenMappingsService,
  PackungseinheitenService,
  PlattformPackungseinheit,
  PlattformPackungseinheitMapping,
  UpdateIndustryCatalog,
  UpdateSupplierOrManufacturer,
} from '@mp/content-manager/industriekataloge/data-access';
import { convertPagination } from '@mp/shared/feature';

@Injectable({ providedIn: 'root' })
export class IndustriekatalogeFacade {
  readonly urlId$: Observable<string> = this.routerFacade.routeParam$(':id').pipe(filterNull());

  private readonly catalogsOpenTasksRefresh$ = new BehaviorSubject<void>(undefined);
  readonly catalogsOpenTasks$: Observable<IndustryCatalogOpenTasks> = combineLatest([
    this.urlId$,
    this.catalogsOpenTasksRefresh$,
  ]).pipe(switchMap(([id]) => this.getOpenTasks(id)));

  private dataSourceFromCatalog: string | undefined;

  private readonly _dataSource$ = new BehaviorSubject(undefined as string | undefined);

  private readonly _industryCatalogsDatasource$ = new BehaviorSubject([] as Array<string>);

  private readonly _kataloge$ = new BehaviorSubject([] as Array<IndustryCatalog>);
  readonly kataloge$ = this._kataloge$.asObservable();

  private readonly _katalog$ = new BehaviorSubject(undefined as IndustryCatalog | undefined | null);
  readonly katalog$ = this._katalog$.asObservable();

  private readonly _pagination$ = new BehaviorSubject(undefined as PaginationMetadata | undefined);
  readonly pagination$ = this._pagination$.asObservable();

  private readonly _isLoading$ = new BehaviorSubject(false as boolean);
  readonly isLoading$ = this._isLoading$.asObservable();

  private readonly _kataloglieferanten$ = new BehaviorSubject([] as Array<IndustryCatalogSupplier>);
  readonly kataloglieferanten$ = this._kataloglieferanten$.asObservable();

  private readonly _lieferantenPagination$ = new BehaviorSubject(undefined as PaginationMetadata | undefined);
  readonly lieferantenPagination$ = this._lieferantenPagination$.asObservable();

  private readonly _kataloghersteller$ = new BehaviorSubject([] as Array<IndustryCatalogManufacturer>);
  readonly kataloghersteller$ = this._kataloghersteller$.asObservable();

  private readonly _herstellerPagination$ = new BehaviorSubject(undefined as PaginationMetadata | undefined);
  readonly herstellerPagination$ = this._herstellerPagination$.asObservable();

  private readonly _plattformPackungseinheitenMappings$ = new BehaviorSubject(
    [] as Array<PlattformPackungseinheitMapping>,
  );
  readonly plattformPackungseinheitenMappings$ = this._plattformPackungseinheitenMappings$.asObservable();

  private readonly _dataSourcePackungseinheitenMappings$ = new BehaviorSubject(
    [] as Array<PlattformPackungseinheitMapping>,
  );
  readonly dataSourcePackungseinheitenMappings$ = this._dataSourcePackungseinheitenMappings$.asObservable();

  private readonly _dataSourcePackagingUnitPagination$ = new BehaviorSubject(
    undefined as PaginationMetadata | undefined,
  );
  readonly dataSourcePackagingUnitPagination$ = this._dataSourcePackagingUnitPagination$.asObservable();

  private readonly _plattformPackungseinheiten$ = new BehaviorSubject([] as PlattformPackungseinheit[]);
  readonly plattformPackungseinheiten$ = this._plattformPackungseinheiten$.asObservable();

  private readonly _einheiten$ = new BehaviorSubject({ mappings: [], units: [] } as PackungseinheitenMapping);
  readonly einheiten$ = this._einheiten$.asObservable();

  private readonly _packagingUnitPagination$ = new BehaviorSubject(undefined as PaginationMetadata | undefined);
  readonly packagingUnitPagination$ = this._packagingUnitPagination$.asObservable();

  private readonly _platformDataSources$ = new BehaviorSubject([] as Array<string>);
  readonly platformDataSources$ = this._platformDataSources$.asObservable();
  readonly industryCatalogsDatasource$ = this._industryCatalogsDatasource$.asObservable();

  constructor(
    private readonly service: IndustriekatalogeService,
    private readonly packungseinheitenService: PackungseinheitenService,
    private readonly packungseinheitenMappingsService: PackungseinheitenMappingsService,
    private readonly routerFacade: RouterFacade,
    private readonly toaster: NotificationService,
  ) {}

  getAll(filterOptions?: IndustryCatalogFilter): void {
    this._isLoading$.next(true);
    const params: HttpParams = this.setQueryParams(filterOptions);
    this.service.getAll(params).subscribe({
      next: ({ data, pagination }) => {
        this._kataloge$.next(data);
        this._pagination$.next(pagination);
        this._isLoading$.next(false);
      },
    });
  }

  getAllInFreigabe(): Observable<Array<IndustryCatalogInRelease>> {
    return this.service.getAllInFreigabe();
  }

  private setQueryParams(filter?: IndustryCatalogFilter): HttpParams {
    let queryParams = QueryParams.build<IndustryCatalog>()
      .page(filter?.page ?? 1)
      .pageSize(filter?.pageSize ?? 50)
      .toHttpParams()
      .append('sort', filter?.sortCriteria ?? 'name');

    if (filter?.searchTerm) {
      queryParams = queryParams.append('searchTerm', filter.searchTerm);
    }

    if (filter?.datasources) {
      queryParams = queryParams.appendAll({
        dataSources: filter.datasources,
      });
    }

    if (filter?.openTasks) {
      filter.openTasks?.forEach((task: string) => {
        queryParams = queryParams.append('opentasks', task);
      });
    }

    return queryParams;
  }

  getKatalogDetail(id: string): Observable<IndustryCatalog | null> {
    this._isLoading$.next(true);

    return this.service.getIndustriekatalogDetail(id).pipe(
      catchError(() => {
        this._isLoading$.next(false);
        return of(null);
      }),
      tap((katalog) => {
        this._isLoading$.next(false);
        this._katalog$.next(katalog);
        this._dataSource$.next(katalog?.dataSource);

        this.getKatalogSubentities(id);
      }),
    );
  }

  getKatalogSubentities(id: string, pagination?: Pagination): void {
    this.getKatalogLieferanten(id, pagination);
    this.getKatalogHersteller(id, pagination);
    this.getEinheiten(id);
  }

  getKatalogLieferanten(id: string, pagination?: Pagination): void {
    const params: HttpParams = this.setParams(pagination);

    this.service.getIndustriekatalogLieferanten(id, params).subscribe({
      next: ({ data, pagination }) => {
        this._kataloglieferanten$.next(data);
        this._lieferantenPagination$.next(pagination);
      },
    });
  }

  getKatalogHersteller(id: string, pagination?: Pagination): void {
    const params: HttpParams = this.setParams(pagination);

    this.service.getIndustriekatalogHersteller(id, params).subscribe({
      next: ({ data, pagination }) => {
        this._kataloghersteller$.next(data);
        this._herstellerPagination$.next(pagination);
      },
    });
  }

  getPlattformPackungseinheiten(): void {
    this.packungseinheitenService.getAll().subscribe({
      next: ({ data }) => {
        this._plattformPackungseinheiten$.next(data);
      },
    });
  }

  clearPlattformPackungseinheitenMappings(): void {
    this._dataSourcePackungseinheitenMappings$.next([]);
    this._dataSourcePackagingUnitPagination$.next(undefined);
  }

  getAllPlattformPackungseinheitenMappingsFromDataSource(datasource: string, pagination?: Pagination): void {
    const httpParams = this.setDataSourceParams(pagination);
    this.packungseinheitenMappingsService.getAll(datasource, httpParams).subscribe({
      next: ({ data, pagination }) => {
        this._dataSourcePackungseinheitenMappings$.next(data);
        this._dataSourcePackagingUnitPagination$.next(pagination);
      },
    });
  }

  getPlattformPackungseinheitenMappings(id: string): void {
    this.packungseinheitenMappingsService.get(id).subscribe({
      next: ({ data }) => this._plattformPackungseinheitenMappings$.next(data),
    });
  }

  getAllDatasources(): void {
    this.packungseinheitenMappingsService
      .getDatenquellen()
      .pipe(getProp('data'))
      .subscribe({
        next: (data) => {
          this._platformDataSources$.next(data);
        },
      });
  }

  getIndustryCatalogsDatasources(): void {
    this.service.getIndustryCatalogsDatasources().subscribe({
      next: (response) => {
        this._industryCatalogsDatasource$.next(response);
      },
    });
  }

  getOpenTasks(id: string): Observable<IndustryCatalogOpenTasks> {
    return this.service.getOpenTasks(id);
  }

  getEinheiten(id: string, pagination?: Pagination): void {
    this._dataSource$.pipe(tap((dataSource) => (this.dataSourceFromCatalog = dataSource))).subscribe();
    const httpParams = this.setDataSourceParams(pagination);

    const mappings$ = this.packungseinheitenMappingsService.get(id, this.dataSourceFromCatalog, httpParams);
    const units$ = this.packungseinheitenService.getAll();

    combineLatest([mappings$, units$]).subscribe(([mappings, units]) => {
      this._einheiten$.next({ mappings: mappings.data, units: units.data });
      this._packagingUnitPagination$.next(mappings.pagination);
    });
  }

  setParams(pagination?: Pagination): HttpParams {
    const queryParams = QueryParams.build<IndustryCatalog>()
      .page(pagination?.page ?? 1)
      .pageSize(pagination?.pageSize ?? 10);

    return queryParams.toHttpParams();
  }

  setDataSourceParams(pagination?: Pagination): HttpParams {
    return QueryParams.build<IndustryCatalog>()
      .page(pagination?.page ?? 1)
      .pageSize(pagination?.pageSize ?? 10)
      .toHttpParams();
  }

  updateLieferantOrHersteller(isLieferantUpdated: boolean, updateDto: UpdateSupplierOrManufacturer): void {
    const toastMessage = updateDto.platformSupplierId
      ? 'Die Zuweisung wurde gespeichert.'
      : 'Die Zuweisung wurde entfernt.';

    // TODO: Dieses Durchschleifen und Abfragen des boolschen Flags von den Komponenten bis hier ist nicht so schön.
    if (isLieferantUpdated) {
      const lieferantenPagination = this._lieferantenPagination$.getValue();
      this.service.updateLieferant(updateDto).subscribe({
        next: () => {
          this.toaster.toastSuccess(toastMessage);
          this.getKatalogLieferanten(updateDto.id, convertPagination(lieferantenPagination));
          this.catalogsOpenTasksRefresh$.next();
        },
      });
    } else {
      const herstellerPagination = this._herstellerPagination$.getValue();
      this.service.updateHersteller(updateDto).subscribe({
        next: () => {
          this.toaster.toastSuccess(toastMessage);
          this.getKatalogHersteller(updateDto.id, convertPagination(herstellerPagination));
          this.catalogsOpenTasksRefresh$.next();
        },
      });
    }
  }

  updateIndustriekatalog(
    catalogId: string,
    updateDto: UpdateIndustryCatalog,
    filterOptions?: IndustryCatalogFilter,
  ): void {
    this.service.updateIndustriekatalog(catalogId, updateDto).subscribe({
      next: () => {
        // TODO: Was passiert sollte ggf. aus der Response kommen
        const message = updateDto.supplierId
          ? 'Die Änderungen wurden gespeichert. Der Industriekatalog wird aktualisiert.'
          : 'Die Änderungen wurden gespeichert.';
        this.toaster.toastSuccess(message);

        if (filterOptions) {
          this.getAll(filterOptions);
        }
      },
    });
  }

  updatePackungseinheit(einheit: PlattformPackungseinheitMapping): void {
    const toastMessage = einheit.mappedShortName ? 'Die Zuweisung wurde gespeichert.' : 'Die Zuweisung wurde entfernt.';
    this.packungseinheitenMappingsService.update(einheit).subscribe(() => {
      const packagingUnitPagination = this._packagingUnitPagination$.getValue();

      this.getEinheiten(einheit.catalogId, convertPagination(packagingUnitPagination));

      this.toaster.toastSuccess(toastMessage);
      this.catalogsOpenTasksRefresh$.next();
    });
  }

  updateDatasourcePackungseinheit(einheit: PlattformPackungseinheitMapping): void {
    const toastMessage = einheit.mappedShortName ? 'Die Zuweisung wurde gespeichert.' : 'Die Zuweisung wurde entfernt.';
    this.packungseinheitenMappingsService.update(einheit).subscribe(() => {
      this.getAllPlattformPackungseinheitenMappingsFromDataSource(
        einheit.dataSource,
        convertPagination(this._dataSourcePackagingUnitPagination$.getValue()),
      );
      this.toaster.toastSuccess(toastMessage);
    });
  }

  navigateToIndustriekatalogDetail(id: string): void {
    this.routerFacade.navigate((router, route) =>
      router.navigate(['./', encodeURIComponent(id)], { relativeTo: route }),
    );
  }

  navigateBack(): void {
    this.routerFacade.back();
  }
}
